import { useTranslation } from 'react-i18next';

import BagIcon from '~/assets/icons/Bag';
import ClockIcon from '~/assets/icons/Clock';
import InformationIcon from '~/assets/icons/Information';
import MailIcon from '~/assets/icons/Mail';
import MarketStallIcon from '~/assets/icons/MarketStall';
import ProfileIcon from '~/assets/icons/Profile';
import SettingsIcon from '~/assets/icons/Settings';
import SneakerOutline from '~/assets/icons/SneakerOutline';
import type { UserProfile } from '~/user/Profile';
import type { Region } from '~/utils/region';

export type ProfileItemType = {
  description: string;
  external?: boolean;
  icon: JSX.Element;
  path: string;
  title: string;
};

export type ProfileSectionType = {
  items: ProfileItemType[];
  title: string;
};

export type ProfilePageContentsType = {
  sections: ProfileSectionType[];
};

export type ProfilePageContentsProps = {
  profile: UserProfile | undefined;
  region?: Region;
};

const regionConfig = {
  DE: ['contactPreferences'],
  default: ['marketplace', 'products', 'account', 'support']
};

export default function ProfilePageContents(
  props: ProfilePageContentsProps
): ProfilePageContentsType {
  const { profile, region } = props;
  const { t } = useTranslation('profile');
  const is_seller = profile?.is_seller || false;

  const allSections = {
    account: {
      getItems: () => [
        {
          description: 'Update your details and preferences',
          external: true,
          icon: <ProfileIcon color="black" height="16" width="16" />,
          path: `/profile/my-account/`,
          title: 'My details'
        },
        {
          description: 'Manage your marketing email preferences',
          external: true,
          icon: <SettingsIcon height="16" width="16" />,
          path: `/profile/contact-preferences/`,
          title: 'Contact preferences'
        },
        {
          description: 'Set your shoe size',
          external: true,
          icon: <SneakerOutline height="16" width="16" />,
          path: `/profile/shoe-size/`,
          title: 'Shoe size'
        }
      ],
      title: 'Account'
    },
    contactPreferences: {
      getItems: () => [
        {
          description: t('contactPreferencesDescription'),
          icon: <SettingsIcon height="16" width="16" />,
          path: `/profile/contact-preferences/`,
          title: t('contactPreferences')
        }
      ],
      title: t('account')
    },
    marketplace: {
      getItems: () => [
        {
          description:
            'View and manage your orders from The Sole Supplier Marketplace',
          external: true,
          icon: <BagIcon height={16} width={16} stroke="black" />,
          path: `/profile/orders/`,
          title: 'My orders'
        },
        {
          description: is_seller
            ? 'Manage your marketplace listings and sales'
            : 'Sell deadstock sneakers on The Sole Supplier Marketplace',
          external: true,
          icon: <MarketStallIcon height="16" width="16" />,
          path: `/marketplace/sell/listings/`,
          title: 'Sell on Marketplace'
        },
        {
          description: 'Answers to common questions',
          icon: (
            <InformationIcon
              height={16}
              width={16}
              stroke="black"
              fill="white"
            />
          ),
          path: 'https://thesolesupplier.zendesk.com/hc/en-gb',
          title: 'FAQs'
        }
      ],
      title: 'Marketplace'
    },
    products: {
      getItems: () => [
        {
          description:
            'Set up alerts when a product releases, restocks or drops in price',
          icon: <SneakerOutline height="16" width="16" />,
          path: '/profile/notifications/',
          title: 'Product notifications'
        },
        {
          description: "Products and articles you've recently viewed",
          external: true,
          icon: <ClockIcon height="16" width="16" />,
          path: `/profile/recently-viewed/`,
          title: 'Recently viewed'
        }
      ],
      title: 'Products & articles'
    },
    support: {
      getItems: () => [
        {
          description: 'Get in touch with our team',
          icon: <MailIcon height="16" width="16" stroke="black" />,
          path: '/contact/',
          title: 'Contact us'
        }
      ],
      title: 'Support'
    }
  };

  const allowedSections =
    regionConfig[region?.code as keyof typeof regionConfig] ||
    regionConfig.default;

  const sections = allowedSections.map((sectionKey) => {
    const section = allSections[sectionKey as keyof typeof allSections];

    return {
      items: section.getItems(),
      title: section.title
    } as ProfileSectionType;
  });

  return { sections };
}
